import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { Button, Modal } from "../components/Modal";
import LoadingSpinner from "../general/Loader";
import {
    CreateDynamicGroupType,
    DynamicGroupFieldOption,
    DynamicGroupPreview,
    GetDynamicGroupFields,
    PreviewDynamicGroups,
} from "./GroupStore";

interface DynamicGroupBuilderProps {
    organizationId: string;
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
}

const emptyPreview: DynamicGroupPreview = {
    totalGroups: 0,
    totalMembers: 0,
    isTruncated: false,
    groups: [],
};

export const DynamicGroupBuilder: React.FC<DynamicGroupBuilderProps> = ({ organizationId, isOpen, onClose, onCreated }) => {
    const [name, setName] = React.useState("");
    const [condition, setCondition] = React.useState("");
    const [fields, setFields] = React.useState([] as DynamicGroupFieldOption[]);
    const [preview, setPreview] = React.useState(emptyPreview);
    const [loadingFields, setLoadingFields] = React.useState(false);
    const [loadingPreview, setLoadingPreview] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        if (!isOpen || !organizationId) return;

        let active = true;
        setName("");
        setCondition("");
        setFields([]);
        setPreview(emptyPreview);
        setError("");
        setLoadingFields(true);

        GetDynamicGroupFields(organizationId)
            .then((result) => {
                if (active) setFields(result);
            })
            .catch(() => {
                if (active) setError("Metadata fields could not be loaded. Please try again.");
            })
            .finally(() => {
                if (active) setLoadingFields(false);
            });

        return () => {
            active = false;
        };
    }, [isOpen, organizationId]);

    React.useEffect(() => {
        if (!isOpen || !organizationId || !condition) {
            setPreview(emptyPreview);
            return;
        }

        let active = true;
        setLoadingPreview(true);
        setError("");

        PreviewDynamicGroups(organizationId, condition)
            .then((result) => {
                if (active) setPreview(result);
            })
            .catch(() => {
                if (active) {
                    setPreview(emptyPreview);
                    setError("The preview could not be generated. Please select another field or try again.");
                }
            })
            .finally(() => {
                if (active) setLoadingPreview(false);
            });

        return () => {
            active = false;
        };
    }, [condition, isOpen, organizationId]);

    const createDynamicGroups = async () => {
        setSaving(true);
        setError("");
        try {
            const response = await CreateDynamicGroupType(organizationId, { name: name.trim(), condition });
            if (!response.success) {
                setError(response.message || "The dynamic groups could not be created.");
                return;
            }

            onCreated();
            onClose();
        } catch (requestError) {
            setError("The dynamic groups could not be created. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Dynamic Group Builder" icon={["fad", "users-cog"]} size="xl" closeOnOverlayClick={!saving}>
            <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[32rem]">
                <div className="p-6 space-y-6 border-b lg:border-b-0 lg:border-r border-gray-200">
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                        Build a self-updating set of groups from user metadata. A group is created for every unique value in the field you choose.
                    </div>

                    <section>
                        <div className="flex items-start gap-3">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">1</span>
                            <div className="w-full">
                                <label className="block text-sm font-semibold text-gray-900 mb-2" htmlFor="dynamic-group-name">
                                    Name this group set
                                </label>
                                <input
                                    id="dynamic-group-name"
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    placeholder="e.g. Classes by registration class"
                                    maxLength={200}
                                    autoFocus
                                />
                                <p className="mt-2 text-xs text-gray-500">This name appears as the group type in filters and group details.</p>
                            </div>
                        </div>
                    </section>

                    <section>
                        <div className="flex items-start gap-3">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">2</span>
                            <div className="w-full">
                                <label className="block text-sm font-semibold text-gray-900 mb-2" htmlFor="dynamic-group-field">
                                    Choose the grouping field
                                </label>
                                {loadingFields ? (
                                    <div className="py-8"><LoadingSpinner /></div>
                                ) : fields.length === 0 ? (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                                        This organization has no user metadata yet, so there are no fields available for dynamic grouping.
                                    </div>
                                ) : (
                                    <>
                                        <select
                                            id="dynamic-group-field"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={condition}
                                            onChange={(event) => setCondition(event.target.value)}
                                        >
                                            <option value="">Select a metadata field</option>
                                            {fields.map((field) => (
                                                <option key={field.key} value={field.key}>
                                                    {field.key} ({field.valueCount} values, {field.memberCount} users)
                                                </option>
                                            ))}
                                        </select>
                                        <div className="mt-4 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                                            <span className="font-medium">For each unique value in</span>
                                            <span className="rounded bg-white border border-gray-300 px-2 py-1 font-mono text-xs">{condition || "metadata field"}</span>
                                            <FontAwesomeIcon icon={["fad", "arrow-right"]} className="text-gray-400" />
                                            <span className="font-medium">create one group</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </section>

                    {error && (
                        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                            {error}
                        </div>
                    )}
                </div>

                <div className="flex flex-col bg-gray-50">
                    <div className="border-b border-gray-200 px-6 py-4">
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={["fad", "eye"]} className="text-blue-600" />
                            <h4 className="font-semibold text-gray-900">Live outcome preview</h4>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">The preview uses current metadata. Membership updates automatically when that data changes.</p>
                    </div>

                    <div className="flex-1 p-6">
                        {loadingPreview ? (
                            <div className="flex h-full min-h-[20rem] items-center justify-center"><LoadingSpinner /></div>
                        ) : !condition ? (
                            <div className="flex h-full min-h-[20rem] flex-col items-center justify-center text-center text-gray-500">
                                <FontAwesomeIcon icon={["fad", "layer-group"]} className="mb-4 text-5xl text-gray-300" />
                                <p className="font-medium text-gray-700">Choose a metadata field</p>
                                <p className="mt-1 max-w-xs text-sm">The groups and member counts that will be produced appear here immediately.</p>
                            </div>
                        ) : preview.totalGroups === 0 ? (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">No groups would be created from this field.</div>
                        ) : (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="rounded-lg border border-blue-200 bg-white p-4">
                                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Groups created</div>
                                        <div className="mt-1 text-2xl font-semibold text-blue-700">{preview.totalGroups}</div>
                                    </div>
                                    <div className="rounded-lg border border-green-200 bg-white p-4">
                                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Unique members</div>
                                        <div className="mt-1 text-2xl font-semibold text-green-700">{preview.totalMembers}</div>
                                    </div>
                                </div>

                                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                                    <div className="max-h-72 overflow-y-auto">
                                        <table className="w-full">
                                            <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Resulting group</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Members</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {preview.groups.map((group, index) => (
                                                    <tr key={`${group.name}-${index}`}>
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{group.name || <span className="italic text-gray-400">Empty value</span>}</td>
                                                        <td className="px-4 py-3 text-right">
                                                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                                                                <FontAwesomeIcon icon={["fad", "user-group"]} /> {group.memberCount}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {preview.isTruncated && <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-500">Showing the first 50 groups.</div>}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-200 bg-white px-6 py-4 flex justify-end gap-3">
                <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="primary" onClick={createDynamicGroups} disabled={saving || !name.trim() || !condition || preview.totalGroups === 0}>
                    {saving ? "Creating..." : preview.totalGroups > 0 ? `Create ${preview.totalGroups} Dynamic Group${preview.totalGroups === 1 ? "" : "s"}` : "Create Dynamic Groups"}
                </Button>
            </div>
        </Modal>
    );
};
